import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ALLOWED_FONTS,
  THEME_MODES,
  checkBrandingContrast,
  type BrandingColors,
  type ThemeMode,
} from '@busbook/shared';
import { Alert, ApiError, Button, Card, Eyebrow, Field, Input, Select } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { api } from '../lib/api.js';
import { useT, type StringKey } from '../i18n/index.js';

/**
 * Branding editor with live preview (docs/04 §3).
 *
 * The preview is the real customer app in an iframe; the draft theme is pushed to
 * it over postMessage, so what the operator sees is the actual product rendering
 * their colours — not a mock-up that can drift from it.
 *
 * IDENTITY ONLY (DECISIONS D69). Name, logo, colours, typeface, light or dark. The menu
 * and the customer-app feature set are the platform's to decide and live in the platform
 * console; this screen does not read them, does not send them and does not model them.
 * The preview frame fills them in from what the tenant already publishes — see the
 * customer app's lib/preview.ts.
 */

interface BrandingResponse {
  appName: string;
  logoUrl: string | null;
  icon192Url: string | null;
  iconMaskableUrl: string | null;
  colors: BrandingColors;
  fontFamily: string;
  themeMode: ThemeMode;
}

/** Labels live as KEYS here and are resolved at render: `t` is a hook, and this
 *  table is module scope. */
const COLOR_FIELDS: Array<{ key: keyof BrandingColors; labelKey: StringKey; hint: string }> = [
  { key: 'primary', labelKey: 'f.primary', hint: 'Buttons, links and the installed app’s theme colour' },
  { key: 'secondary', labelKey: 'f.secondary', hint: 'Accents and secondary highlights' },
  { key: 'background', labelKey: 'f.background', hint: 'The page behind everything' },
  { key: 'surface', labelKey: 'f.surface', hint: 'Cards, sheets and the ticket' },
  { key: 'text', labelKey: 'f.text', hint: 'All body copy' },
];

function previewOrigin(slug: string): string {
  const base = import.meta.env.VITE_CUSTOMER_ORIGIN_TEMPLATE ?? 'http://{slug}.busbook.localhost:5173';
  return base.replace('{slug}', slug);
}

export function Branding({ tenantSlug }: { tenantSlug: string }) {
  const t = useT();
  const [draft, setDraft] = useState<BrandingResponse | null>(null);
  const [saved, setSaved] = useState<BrandingResponse | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'uploading'>('idle');
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const origin = useMemo(() => previewOrigin(tenantSlug), [tenantSlug]);

  useEffect(() => {
    void (async () => {
      const result = await api.get<{ branding: BrandingResponse }>('/admin/branding');
      // Only the identity fields are kept. The response still carries the platform-owned
      // menu and feature lists; reading them here would put this screen back in the
      // business of describing a decision it does not make.
      const identity: BrandingResponse = {
        appName: result.branding.appName,
        logoUrl: result.branding.logoUrl,
        icon192Url: result.branding.icon192Url,
        iconMaskableUrl: result.branding.iconMaskableUrl,
        colors: result.branding.colors,
        fontFamily: result.branding.fontFamily,
        themeMode: result.branding.themeMode,
      };
      setDraft(identity);
      setSaved(identity);
    })();
  }, []);

  /** Push the draft into the preview frame whenever it changes. */
  const pushPreview = useCallback(() => {
    if (!draft || !frame.current?.contentWindow) return;
    frame.current.contentWindow.postMessage(
      {
        type: 'busbook:preview-branding',
        branding: {
          appName: draft.appName,
          logoUrl: draft.logoUrl,
          iconUrl: draft.icon192Url,
          colors: draft.colors,
          fontFamily: draft.fontFamily,
          themeMode: draft.themeMode,
          // menuStyle, menuItems and customerFeatures are deliberately absent. The frame
          // keeps whatever this tenant publishes for them, so it still renders the real
          // navigation without this screen claiming to set it.
        },
      },
      origin,
    );
  }, [draft, origin]);

  useEffect(() => {
    pushPreview();
  }, [pushPreview]);

  const contrast = useMemo(
    () => (draft ? checkBrandingContrast(draft.colors) : null),
    [draft],
  );

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved],
  );

  if (!draft) {
    return (
      <Page title={t('nav.branding')}>
        <p style={{ color: 'var(--color-muted)' }}>Loading…</p>
      </Page>
    );
  }

  function update(patch: Partial<BrandingResponse>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setMessage(null);
  }

  function updateColor(key: keyof BrandingColors, value: string) {
    setDraft((current) =>
      current ? { ...current, colors: { ...current.colors, [key]: value } } : current,
    );
    setMessage(null);
  }

  async function save() {
    if (!draft) return;
    setStatus('saving');
    setMessage(null);
    try {
      const result = await api.put<{ branding: BrandingResponse }>('/admin/branding', {
        appName: draft.appName,
        colors: draft.colors,
        fontFamily: draft.fontFamily,
        themeMode: draft.themeMode,
        // menuStyle, menuItems and customerFeatures are absent deliberately (DECISIONS D69).
        // The API would strip them anyway; sending them would be this console claiming a
        // decision it does not make.
      });
      // Spread only the identity fields back: the response is the full branding record,
      // and folding the platform-owned lists into the draft would reintroduce them here
      // through the back door and make the screen look dirty for a field it cannot edit.
      const identity: BrandingResponse = {
        appName: result.branding.appName,
        logoUrl: result.branding.logoUrl,
        icon192Url: result.branding.icon192Url,
        iconMaskableUrl: result.branding.iconMaskableUrl,
        colors: result.branding.colors,
        fontFamily: result.branding.fontFamily,
        themeMode: result.branding.themeMode,
      };
      setSaved(identity);
      setDraft(identity);
      setMessage({ tone: 'success', text: 'Branding published. Passengers see it on next load.' });
      frame.current?.contentWindow?.location.reload();
    } catch (error) {
      setMessage({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : 'Could not publish branding. Try again.',
      });
    } finally {
      setStatus('idle');
    }
  }

  async function uploadLogo(file: File) {
    setStatus('uploading');
    setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const icons = await api.upload<{
        logoUrl: string;
        icon192Url: string;
        icon512Url: string;
        iconMaskableUrl: string;
      }>('/admin/branding/logo', form);
      update({
        logoUrl: icons.logoUrl,
        icon192Url: icons.icon192Url,
        iconMaskableUrl: icons.iconMaskableUrl,
      });
      // The same three fields the draft took, not the whole upload response: it also
      // carries icon512Url, which the draft has no field for, and folding it in here
      // alone made `saved` permanently differ from `draft` — leaving the screen showing
      // unpublished changes for a logo that was already saved.
      setSaved((current) =>
        current
          ? {
              ...current,
              logoUrl: icons.logoUrl,
              icon192Url: icons.icon192Url,
              iconMaskableUrl: icons.iconMaskableUrl,
            }
          : current,
      );
      setMessage({ tone: 'success', text: 'Logo uploaded. App icons were generated from it.' });
    } catch (error) {
      setMessage({
        tone: 'error',
        text: error instanceof ApiError ? error.message : 'Upload failed. Try a PNG or SVG.',
      });
    } finally {
      setStatus('idle');
    }
  }

  return (
    <Page
      title={t('nav.branding')}
      description={t('br.subtitle')}
      actions={
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {t('br.unpublished')}
            </span>
          )}
          <Button
            onClick={save}
            loading={status === 'saving'}
            disabled={!dirty || (contrast?.blocking.length ?? 0) > 0}
          >
            {t('f.publish')}
          </Button>
        </div>
      }
    >
      {message && (
        <div className="mb-4">
          <Alert tone={message.tone === 'success' ? 'success' : 'error'}>{message.text}</Alert>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="flex flex-col gap-5">
          <Card>
            <Eyebrow>{t('br.identity')}</Eyebrow>
            <div className="mt-3 flex flex-col gap-4">
              <Field
                label={t('br.appName')}
                htmlFor="appName"
                hint={t('br.appNameHint')}
              >
                <Input
                  id="appName"
                  value={draft.appName}
                  maxLength={20}
                  onChange={(e) => update({ appName: e.target.value })}
                />
              </Field>

              <div>
                <p className="mb-1.5 text-sm font-medium">{t('f.logo')}</p>
                <div className="flex items-center gap-4">
                  <div
                    className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-lg border"
                    style={{ borderColor: 'var(--color-line)', background: draft.colors.background }}
                  >
                    {draft.logoUrl ? (
                      <img src={draft.logoUrl} alt="" className="max-h-14 max-w-14 object-contain" />
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--color-faint)' }}>
                        {t('f.none')}
                      </span>
                    )}
                  </div>
                  <div>
                    <input
                      id="logo"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="text-sm"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadLogo(file);
                      }}
                    />
                    <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                      PNG, JPEG, WebP or SVG, up to 2 MB. App icons are generated automatically.
                    </p>
                  </div>
                </div>
                {draft.iconMaskableUrl && (
                  <div className="mt-3 flex items-center gap-3">
                    <img
                      src={draft.iconMaskableUrl}
                      alt="Maskable app icon preview"
                      className="h-12 w-12 rounded-[22%]"
                    />
                    <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                      {t('br.maskable')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <Eyebrow>{t('f.colours')}</Eyebrow>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {COLOR_FIELDS.map((field) => (
                <Field key={field.key} label={t(field.labelKey)} hint={field.hint}>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label={`${t(field.labelKey)} colour`}
                      value={draft.colors[field.key]}
                      onChange={(e) => updateColor(field.key, e.target.value.toUpperCase())}
                      className="h-11 w-12 shrink-0 cursor-pointer rounded-lg border"
                      style={{ borderColor: 'var(--color-line-strong)', background: 'transparent' }}
                    />
                    <Input
                      value={draft.colors[field.key]}
                      aria-label={`${t(field.labelKey)} hex value`}
                      onChange={(e) => updateColor(field.key, e.target.value.toUpperCase())}
                      className="tabular"
                    />
                  </div>
                </Field>
              ))}
            </div>

            {contrast && contrast.blocking.length > 0 && (
              <div className="mt-4">
                <Alert tone="error" title={t('br.notReadable')}>
                  <ul className="mt-1 list-disc pl-4">
                    {contrast.blocking.map((warning) => (
                      <li key={warning.pair}>{warning.message}</li>
                    ))}
                  </ul>
                  <p className="mt-1">{t('br.fixBefore')}</p>
                </Alert>
              </div>
            )}

            {contrast && contrast.advisory.length > 0 && (
              <div className="mt-3">
                <Alert tone="warning" title={t('br.worthLook')}>
                  <ul className="mt-1 list-disc pl-4">
                    {contrast.advisory.map((warning) => (
                      <li key={warning.pair}>{warning.message}</li>
                    ))}
                  </ul>
                  <p className="mt-1">{t('br.canPublish')}</p>
                </Alert>
              </div>
            )}
          </Card>

          <Card>
            <Eyebrow>{t('br.typeTheme')}</Eyebrow>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Field label={t('f.font')} htmlFor="font">
                <Select
                  id="font"
                  value={draft.fontFamily}
                  onChange={(e) => update({ fontFamily: e.target.value })}
                >
                  {ALLOWED_FONTS.map((font) => (
                    <option key={font} value={font}>
                      {font === 'system' ? 'System default' : font}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('br.themeMode')} htmlFor="themeMode">
                <Select
                  id="themeMode"
                  value={draft.themeMode}
                  onChange={(e) => update({ themeMode: e.target.value as ThemeMode })}
                >
                  {THEME_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode === 'AUTO' ? 'Follow device' : mode === 'LIGHT' ? 'Light' : 'Dark'}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Card>

          {/*
            The menu and the customer-app features used to live here. They moved to the
            platform console (DECISIONS D69): they describe the SHAPE of the product an
            operator was sold, not their identity within it, so switching one on is a
            commercial decision rather than a preference. Colours, name, logo and theme —
            everything above — remain the operator's own. Nothing on this screen reads or
            writes them any more, not even to feed the preview.
          */}
        </div>

        <div className="xl:sticky xl:top-6 xl:self-start">
          <Card>
            <div className="flex items-center justify-between">
              <Eyebrow>{t('br.livePreview')}</Eyebrow>
              <a
                href={origin}
                target="_blank"
                rel="noreferrer"
                className="text-xs underline underline-offset-2"
                style={{ color: 'var(--color-primary)' }}
              >
                {t('br.openApp')}
              </a>
            </div>
            <div
              className="mt-3 overflow-hidden rounded-[1.75rem] border-[6px]"
              style={{ borderColor: '#10161F', background: '#10161F' }}
            >
              <iframe
                ref={frame}
                title={t('br.preview')}
                src={origin}
                onLoad={pushPreview}
                width={360}
                height={640}
                className="block bg-white"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
            <p className="mt-3 text-xs" style={{ color: 'var(--color-muted)' }}>
              {t('br.previewHint')}
            </p>
          </Card>
        </div>
      </div>
    </Page>
  );
}
